import { isValid } from 'date-fns';
import LangEnum from '../../../lang/LangEnum';
import { formatDate } from '../../../utils/dates';
import {
  CATALOG_ACCESS_AVAILABLE_PATH_BY_FIELD,
  CATALOG_CREATION_ACCESS_ADD_CONFIG_BY_FIELD,
  CATALOG_CREATION_ACCESS_CATALOG_DATE_FORMAT,
  CATALOG_CREATION_ACCESS_TABLE_HEADER_HEIGHT,
  CATALOG_CREATION_ACCESS_TABLE_MAX_VISIBLE_ROWS,
  CATALOG_CREATION_ACCESS_TABLE_ROW_HEIGHT,
} from './catalogCreationAccess.consts';
import {
  AddCatalogCreationAccessEntitiesParams,
  BuildCatalogCreationAccessEntityNamesParams,
  BuildCatalogCreationAccessRowsParams,
  CatalogCreationAccessAddConfig,
  CatalogCreationAccessEntitiesByField,
  CatalogCreationAccessEntity,
  CatalogCreationAccessEntityNamesById,
  CatalogCreationAccessEntityOption,
  CatalogCreationAccessRow,
  ChangeCatalogCreationAccessEntityDateParams,
  FilterListedCatalogCreationAccessOptionsParams,
  RemoveCatalogCreationAccessEntityParams,
  ResolveCatalogAccessDateHelperTextParams,
  ResolveCatalogCreationAccessAddConfigParams,
  ResolveCatalogCreationAccessEmptyLabelParams,
  ResolveCatalogCreationAccessOptionLabelParams,
  UseCatalogCreationAccessTableParams,
} from './catalogCreationAccess.types';

const toValidDateOrNull = (date: Date | null) => {
  if (!date || !isValid(date)) {
    return null;
  }

  return date;
};

export const buildCatalogCreationAccessRows = ({
  entities,
  entityNamesById,
}: BuildCatalogCreationAccessRowsParams): CatalogCreationAccessRow[] =>
  entities.map((entity) => ({
    id: entity.id,
    name: entityNamesById[entity.id] ?? entity.id,
    startDate: entity.startDate,
    endDate: entity.endDate,
  }));

export const hasNoCatalogCreationAccessEntities = (
  entitiesByField: CatalogCreationAccessEntitiesByField,
) => Object.values(entitiesByField).every((entities) => entities.length === 0);

export const removeCatalogCreationAccessEntity = ({
  entities,
  entityId,
}: RemoveCatalogCreationAccessEntityParams) => entities.filter((entity) => entity.id !== entityId);

export const changeCatalogCreationAccessEntityDate = ({
  entities,
  entityId,
  dateField,
  date,
}: ChangeCatalogCreationAccessEntityDateParams) => {
  if (date !== null && !isValid(date)) {
    return entities;
  }

  return entities.map((entity) => {
    if (entity.id !== entityId) {
      return entity;
    }

    return { ...entity, [dateField]: date };
  });
};

export const addCatalogCreationAccessEntities = ({
  entities,
  entityIds,
  startDate,
  endDate,
}: AddCatalogCreationAccessEntitiesParams): CatalogCreationAccessEntity[] => {
  const addedDates = {
    startDate: toValidDateOrNull(startDate),
    endDate: toValidDateOrNull(endDate),
  };

  const addedIds = new Set(entityIds);
  const existingIds = new Set(entities.map((entity) => entity.id));

  const updatedEntities = entities.map((entity) => {
    if (!addedIds.has(entity.id)) {
      return entity;
    }

    return {
      ...entity,
      startDate: addedDates.startDate ?? entity.startDate,
      endDate: addedDates.endDate ?? entity.endDate,
    };
  });

  const createdEntities = [...addedIds]
    .filter((entityId) => !existingIds.has(entityId))
    .map((entityId) => ({ id: entityId, ...addedDates }));

  return [...updatedEntities, ...createdEntities];
};

export const resolveCatalogCreationAccessAddConfig = ({
  field,
  catalogId,
}: ResolveCatalogCreationAccessAddConfigParams): CatalogCreationAccessAddConfig => {
  const config = CATALOG_CREATION_ACCESS_ADD_CONFIG_BY_FIELD[field];

  if (!catalogId) {
    return config;
  }

  return {
    ...config,
    source: {
      ...config.source,
      url: `catalogs/${catalogId}/${CATALOG_ACCESS_AVAILABLE_PATH_BY_FIELD[field]}`,
      queryField: 'query',
    },
  };
};

export const filterListedCatalogCreationAccessOptions = ({
  options,
  listedEntityIds,
}: FilterListedCatalogCreationAccessOptionsParams): CatalogCreationAccessEntityOption[] => {
  const listedIds = new Set(listedEntityIds);

  return options.filter((option) => !listedIds.has(option.id));
};

export const resolveCatalogCreationAccessOptionLabel = ({
  option,
  labelField,
}: ResolveCatalogCreationAccessOptionLabelParams) => option[labelField] ?? option.id;

export const buildCatalogCreationAccessEntityNames = ({
  options,
  labelField,
}: BuildCatalogCreationAccessEntityNamesParams): CatalogCreationAccessEntityNamesById =>
  Object.fromEntries(
    options.map((option) => [
      option.id,
      resolveCatalogCreationAccessOptionLabel({ option, labelField }),
    ]),
  );

export const formatCatalogCreationAccessCatalogDate = (catalogDate: Date | null) =>
  formatDate(catalogDate, CATALOG_CREATION_ACCESS_CATALOG_DATE_FORMAT);

export const resolveCatalogAccessDateHelperText = ({
  translate,
  fieldLabel,
  catalogDate,
}: ResolveCatalogAccessDateHelperTextParams) =>
  translate(LangEnum.CATALOGS_ENTITY_DATE_OF_CATALOG, {
    fieldName: translate(fieldLabel),
    date:
      formatCatalogCreationAccessCatalogDate(catalogDate) || translate(LangEnum.GLOBAL_NOT_DEFINED),
  });

export const resolveCatalogCreationAccessEmptyLabel = ({
  hasNoEntities,
  rowCount,
}: ResolveCatalogCreationAccessEmptyLabelParams): LangEnum | null => {
  if (hasNoEntities) {
    return LangEnum.CATALOGS_CREATION_MODAL_ACCESS_RELEASED_TO_EVERYONE;
  }

  if (rowCount === 0) {
    return LangEnum.GLOBAL_SEARCH_EMPTY_RESULTS;
  }

  return null;
};

export const resolveCatalogCreationAccessTableHeight = ({
  rowCount,
  maxVisibleRows = CATALOG_CREATION_ACCESS_TABLE_MAX_VISIBLE_ROWS,
}: UseCatalogCreationAccessTableParams) => {
  const visibleRowCount = Math.min(Math.max(rowCount, 1), maxVisibleRows);

  return (
    CATALOG_CREATION_ACCESS_TABLE_HEADER_HEIGHT +
    visibleRowCount * CATALOG_CREATION_ACCESS_TABLE_ROW_HEIGHT
  );
};
